import { Inject, Injectable } from '@angular/core';
import { EnumTranslator, L10n } from '@skylab-angular-shared/core';
import { BusinessObjectBase, L10nBase, LocalContext, ModelBase, RepositoryBase } from '@skylab-shared/core';
import { EnumTranslatorBase, InventoryEffect, LabeledEnumValue, LabeledEnumValueConverter } from '@skylab-shared/dataModel';
import { FormBase, FormBaseViewModel } from '../form/form-base';

export type DocumentFormViewModelInventoryEffectExtension = {
    inventoryEffects: LabeledEnumValue[];
    inventoryEffectValueConverter: LabeledEnumValueConverter<InventoryEffect>;
    getInventoryEffectTooltip: () => string;
    isInventoryEffectEmptyItemVisible: () => boolean;
    inventoryEffectEmptyItem: LabeledEnumValue;
    inventoryEffectEmptyLabel: string;
    isSaleGoodsIssueDateVisible: () => boolean;
};

interface DocumentModel extends ModelBase {
    inventoryEffect: InventoryEffect | null;
}

@Injectable({ providedIn: 'root' })
export class DocumentFormViewModelInventoryEffectExtensionService {
    public constructor(
        @Inject(EnumTranslator) private readonly enumTranslator: EnumTranslatorBase,
        @Inject(L10n) private readonly l10n: L10nBase
    ) {}

    public extendViewModel<TViewModelPart, TViewModel extends TViewModelPart & FormBaseViewModel, TBO extends BusinessObjectBase<TModel>, TModel extends ModelBase & DocumentModel>(
        viewModel: TViewModelPart,
        formComponent: FormBase<TViewModel, RepositoryBase<TBO, TModel>, TBO, TModel>,
        localContext: LocalContext): TViewModelPart & DocumentFormViewModelInventoryEffectExtension {
        return Object.assign(viewModel as any, this.getViewModelExtension(formComponent, localContext));
    }

    private getViewModelExtension<TViewModel extends FormBaseViewModel, TBO extends BusinessObjectBase<TModel>, TModel extends ModelBase & DocumentModel>(
        formComponent: FormBase<TViewModel, RepositoryBase<TBO, TModel>, TBO, TModel>,
        localContext: LocalContext): DocumentFormViewModelInventoryEffectExtension {
        const model = formComponent.bo.model;

        const viewModelExtension: DocumentFormViewModelInventoryEffectExtension = {
            inventoryEffectEmptyItem: {value: null as any, label: this.l10n._T('scenes.shared.document-form.extensions.inventory-effect.field.empty')},
            inventoryEffectEmptyLabel: this.l10n._T('scenes.shared.document-form.extensions.inventory-effect.field.label'),
            inventoryEffects: this.enumTranslator.getLabeledEnumValueArray(InventoryEffect),
            inventoryEffectValueConverter: new LabeledEnumValueConverter<InventoryEffect>(this.enumTranslator, InventoryEffect),
            getInventoryEffectTooltip: (): string => {
                if (!model.inventoryEffect) return viewModelExtension.inventoryEffectEmptyItem.label;
                return this.enumTranslator.getLabeledEnumValue(InventoryEffect, model.inventoryEffect).label;
            },
            isInventoryEffectEmptyItemVisible: (): boolean => {
                return !formComponent.bo.model.inventoryEffect;
            },
            isSaleGoodsIssueDateVisible: (): boolean => {
                return !!formComponent.bo.model.inventoryEffect;
            }
        };

        return viewModelExtension;
    }
}
